import { useCallback, useMemo, useState } from 'react';
import type { ZodType } from 'zod';

import { ScopedStorage } from '@suntex.core/storage';
import { useAuth } from '@suntex.fim/auth/react';

function readStorage<T>(
  storage: ScopedStorage | null,
  key: string,
  schema: ZodType<T>,
): T {
  const raw = storage?.getItem(key);
  let json: unknown;
  try {
    json = raw == null ? undefined : JSON.parse(raw);
  } catch {
    json = undefined;
  }
  const result = schema.safeParse(json);
  // Every schema passed to this hook must be fully satisfiable from `{}` via
  // .catch()/.default() on each field, so this fallback always succeeds.
  return result.success ? result.data : schema.parse({});
}

/**
 * `schema` must be fully parseable from `{}` (every field needs `.default()`/`.catch()`), since that's the fallback-recovery contract used to recover from stale/partial persisted values.
 */
export function usePersistedState<T>(
  key: string,
  schema: ZodType<T>,
): [T, (value: T) => void] {
  const { login } = useAuth();

  const storage = useMemo(
    () =>
      login?.id !== undefined
        ? new ScopedStorage(localStorage, `user:${login.id}:`)
        : null,
    [login?.id],
  );

  const [value, setLocal] = useState<T>(() =>
    readStorage(storage, key, schema),
  );

  // React-recommended pattern for resetting state when a dependency changes:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prev, setPrev] = useState({ storage, key });
  if (storage !== prev.storage || key !== prev.key) {
    setPrev({ storage, key });
    setLocal(readStorage(storage, key, schema));
  }

  const setValue = useCallback(
    (next: T) => {
      setLocal(next);
      storage?.setItem(key, JSON.stringify(next));
    },
    [storage, key],
  );

  return [value, setValue];
}
